import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import CurrencyInput from '../shared/CurrencyInput';
import TrustFilieBadge from '../shared/TrustFilieBadge';
import {
  markFieldOverride,
  updateIncomeSources,
} from '../../store/questionnaireSlice';

const docTypeLabels: Record<string, string> = {
  'W-2': 'W-2 Wage & Tax Statement',
  '1099-NEC': '1099-NEC Nonemployee Compensation',
  '1099-MISC': '1099-MISC Miscellaneous Income',
  '1099-INT': '1099-INT Interest Income',
  '1099-DIV': '1099-DIV Dividend Income',
  '1099-B': '1099-B Proceeds from Broker',
  '1099-R': '1099-R Retirement Distributions',
  '1099-G': '1099-G Government Payments',
  '1099-SSA': 'SSA-1099 Social Security',
  'K-1': 'Schedule K-1 Partnership/S-Corp',
  '1098': '1098 Mortgage Interest',
  '1098-T': '1098-T Tuition Statement',
  '1098-E': '1098-E Student Loan Interest',
};

const docTypeIcons: Record<string, string> = {
  'W-2': 'W2',
  '1099-NEC': 'NEC',
  '1099-MISC': 'MISC',
  '1099-INT': 'INT',
  '1099-DIV': 'DIV',
  '1099-B': 'B',
  '1099-R': 'R',
  '1099-G': 'G',
  '1099-SSA': 'SSA',
  'K-1': 'K1',
  '1098': '1098',
  '1098-T': 'T',
  '1098-E': 'E',
};

const additionalIncomeTypes = [
  { id: 'rental', label: 'Rental Income' },
  { id: 'retirement', label: 'Retirement Distributions' },
  { id: 'self-employment', label: 'Self-Employment Income' },
  { id: 'other', label: 'Other Income' },
];

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function IncomeReviewStep() {
  const dispatch = useDispatch();
  const { smartDefaults, fieldOverrides, answers } = useSelector(
    (state: RootState) => state.questionnaire
  );

  const incomeSources = smartDefaults?.incomeSources || [];
  const hasSmartDefaults = smartDefaults !== null && incomeSources.length > 0;

  const [isComplete, setIsComplete] = useState(true);
  const [additionalSources, setAdditionalSources] = useState<string[]>(
    answers.incomeSources?.filter(
      (s) => !incomeSources.some((d) => d.type === s)
    ) || []
  );

  // Track local amount overrides for detected documents
  const [amountOverrides, setAmountOverrides] = useState<
    Record<string, number>
  >({});

  const handleAmountChange = (docId: number, field: string, value: number) => {
    const key = `income_${docId}_${field}`;
    setAmountOverrides((prev) => ({ ...prev, [key]: value }));
    dispatch(markFieldOverride(key));
  };

  const getAmount = (docId: number, field: string, original: number) => {
    const key = `income_${docId}_${field}`;
    return amountOverrides[key] !== undefined
      ? amountOverrides[key]
      : original;
  };

  const toggleAdditionalSource = (id: string) => {
    const updated = additionalSources.includes(id)
      ? additionalSources.filter((s) => s !== id)
      : [...additionalSources, id];
    setAdditionalSources(updated);

    // Merge with detected sources for Redux
    const detectedTypes = incomeSources.map((s) => s.type);
    dispatch(updateIncomeSources([...detectedTypes, ...updated]));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          We detected these income sources
        </h2>
        <p className="text-gray-500 mt-1">
          Review the amounts extracted from your documents
        </p>
      </div>

      {/* No income detected */}
      {!hasSmartDefaults && (
        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 text-center">
          <p className="text-gray-500">
            No income detected yet. Upload and analyze your tax documents to
            auto-populate income information.
          </p>
        </div>
      )}

      {/* Income source cards */}
      {incomeSources.map((source) => (
        <div
          key={source.type}
          className="border border-gray-200 rounded-lg overflow-hidden"
        >
          {/* Source header */}
          <div className="bg-gray-50 px-5 py-3 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gray-200 text-xs font-bold text-gray-700">
                {docTypeIcons[source.type] || source.type.slice(0, 3)}
              </span>
              <div>
                <p className="font-semibold text-gray-900 text-sm">
                  {docTypeLabels[source.type] || source.type}
                </p>
                <p className="text-xs text-gray-500">
                  {source.documents.length} document
                  {source.documents.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <TrustFilieBadge source="document" documentType={source.type} />
          </div>

          {/* Documents within this source */}
          <div className="divide-y divide-gray-100">
            {source.documents.map((doc) => {
              const amountKey = `income_${doc.id}_amount`;
              const isOverridden = fieldOverrides[amountKey];

              return (
                <div key={doc.id} className="px-5 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.issuer}
                      </p>
                      {doc.recipientName && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Recipient: {doc.recipientName}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 w-40 flex-shrink-0">
                      <CurrencyInput
                        value={getAmount(doc.id, 'amount', doc.amount)}
                        onChange={(val) =>
                          handleAmountChange(doc.id, 'amount', val)
                        }
                      />
                      {isOverridden && (
                        <p className="text-xs text-gray-400 mt-1 text-right">
                          Originally {formatCurrency(doc.amount)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Is this complete? */}
      {hasSmartDefaults && (
        <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-900 text-sm">
                Is this complete?
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Do these documents represent all your income for the year?
              </p>
            </div>
            <button
              onClick={() => setIsComplete(!isComplete)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isComplete ? 'bg-gray-900' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isComplete ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {isComplete ? 'Yes' : 'No'} — all income accounted for
          </p>

          {/* Additional income checkboxes */}
          {!isComplete && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-2">
                What other income do you have?
              </p>
              {additionalIncomeTypes.map((type) => (
                <label
                  key={type.id}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={additionalSources.includes(type.id)}
                    onChange={() => toggleAdditionalSource(type.id)}
                    className="w-4 h-4 text-gray-600 rounded cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-gray-900">
                    {type.label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Withholding Summary */}
      {hasSmartDefaults &&
        (smartDefaults!.totalWithholding > 0 ||
          smartDefaults!.stateWithholding > 0) && (
          <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">
              Withholding Summary
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Federal Withholding
                </span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(smartDefaults!.totalWithholding)}
                  </span>
                  <TrustFilieBadge source="document" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  State Withholding
                </span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(smartDefaults!.stateWithholding)}
                  </span>
                  <TrustFilieBadge source="document" />
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
